import { useRef, useState } from 'react'
import { PageHeader, Panel, Button, Chip, Field, EmptyState, ConfirmDialog, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, del } from '../lib/api'
import { dateShort, ago, num } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Copy = { id: string; area_code: string; list_date: string; numbers: number; filename: string | null; uploaded_at: string; age_days: number; stale: boolean }
type Data = {
  copies: Copy[]; fresh_days: number
  own: { id: string; number: string; reason: string | null; created_at: string; added_by_name: string | null }[]
  checks: { number: string; list: string; area_code: string | null; list_date: string | null; result: string; checked_at: string; summary: string | null }[]
  last_30_days: { checks: number; listed: number; unchecked: number }
}
const ARIZONA = ['480', '520', '602', '623', '928']
const phone = (n: string) => `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`
const todayPhoenix = () => new Date(Date.now() - 7 * 3600000).toISOString().slice(0, 10)

/**
 * Do Not Call (Business Terms 9.4): the Registry copy for each area code, no
 * more than 31 days old; Handpicked's own do-not-contact list; and what the
 * checks found. Every number on a lead is checked before the lead goes out.
 */
export default function DoNotCall() {
  const d = useApi<Data>('/api/dnc')
  if (d.error) return <ErrorBlock error={d.error} />
  if (!d.data) return <LoadingBlock />
  const have = new Set(d.data.copies.map((c) => c.area_code))
  return (
    <>
      <PageHeader title="Do Not Call" subtitle="Every phone number on a lead is checked before the lead goes out. A number on a list is removed; the lead and the profile link stay." />
      <div className="stack">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">The Registry, by area code</h2><span className="muted">a copy counts for {d.data.fresh_days} days</span></div>
          <p className="muted" style={{ marginTop: 0 }}>Download each area code's file from the FTC's Do Not Call site under each business's SAN, then upload it here. Arizona's five area codes are free. A copy over {d.data.fresh_days} days old stops counting, and leads with those numbers say they were not checked.</p>
          <div className="bill-list">
            {d.data.copies.map((c) => (
              <div key={c.id} className="bill-row">
                <strong>{c.area_code}</strong>
                <span className="muted">{num(c.numbers)} numbers · downloaded {dateShort(`${c.list_date}T12:00:00`)}</span>
                <Chip tone={c.stale ? 'neg' : c.age_days >= d.data!.fresh_days - 5 ? 'warn' : 'pos'} dot>{c.stale ? `${c.age_days} days: not counting` : `${c.age_days} day${c.age_days === 1 ? '' : 's'} old`}</Chip>
              </div>
            ))}
            {ARIZONA.filter((a) => !have.has(a)).map((a) => (
              <div key={a} className="bill-row"><strong>{a}</strong><span className="muted">No copy yet</span><Chip tone="warn" dot>Not checked</Chip></div>
            ))}
          </div>
          <Upload onDone={d.reload} />
        </Panel>
        <OwnList data={d.data} onChanged={d.reload} />
        <Panel>
          <div className="panel-header"><h2 className="panel-title">What the checks found</h2>
            <span className="muted">last 30 days: {d.data.last_30_days.checks} looks, {d.data.last_30_days.listed} on a list, {d.data.last_30_days.unchecked} not checked</span></div>
          {d.data.checks.length === 0 ? <EmptyState title="Nothing flagged" body="Numbers on a list, or not checked, show here. Every check is kept for 4 years." /> : (
            <div className="bill-list">
              {d.data.checks.map((c, i) => (
                <div key={i} className="bill-row">
                  <span className="num">{phone(c.number)}</span>
                  <span className="muted">{c.summary || 'a lead'} · {c.list === 'own' ? 'our list' : `Registry ${c.area_code}${c.list_date ? `, ${dateShort(`${c.list_date}T12:00:00`)}` : ''}`}</span>
                  <Chip tone={c.result === 'listed' ? 'neg' : 'warn'}>{c.result === 'listed' ? 'Removed' : 'Not checked'} {ago(c.checked_at)}</Chip>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  )
}

function Upload({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const file = useRef<HTMLInputElement>(null)
  const [area, setArea] = useState('480')
  const [day, setDay] = useState(todayPhoenix())
  const [busy, setBusy] = useState(false)
  const send = async (f: File | undefined) => {
    if (!f) return
    if (/\.zip$/i.test(f.name)) { toast('Unzip the FTC download first, then upload the text file inside', 'neg'); return }
    setBusy(true)
    try {
      const res = await fetch(`/api/dnc/registry?${new URLSearchParams({ area_code: area, list_date: day, filename: f.name })}`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: await f.text() })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'The upload did not work')
      toast(`${area}: ${num(body.numbers)} numbers${body.other_area ? `, ${num(body.other_area)} from other area codes left out` : ''}`, 'pos')
      onDone()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <div className="form-grid" style={{ marginTop: 'var(--s-5)', alignItems: 'end' }}>
      <Field label="Area code" id="dnc-area" inputMode="numeric" maxLength={3} value={area} onChange={(e) => setArea(e.target.value.replace(/\D/g, ''))} />
      <Field label="Downloaded on" id="dnc-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      <div>
        <input ref={file} type="file" accept=".txt,.csv,text/plain" hidden onChange={(e) => { void send(e.target.files?.[0]); e.target.value = '' }} />
        <Button onClick={() => file.current?.click()} disabled={busy || area.length !== 3}>{busy ? 'Uploading' : 'Upload the FTC file'}</Button>
      </div>
    </div>
  )
}

function OwnList({ data, onChanged }: { data: Data; onChanged: () => void }) {
  const toast = useToast()
  const [n, setN] = useState('')
  const [why, setWhy] = useState('')
  const [removing, setRemoving] = useState<string | null>(null)
  const add = async () => {
    try {
      const r = await post<{ already: boolean; leads_rechecked: number }>('/api/dnc/own', { number: n, reason: why })
      toast(r.already ? 'Already on the list' : `Added${r.leads_rechecked ? `. Removed from ${r.leads_rechecked} lead${r.leads_rechecked === 1 ? '' : 's'} not yet sent.` : ''}`, 'pos')
      setN(''); setWhy(''); onChanged()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">Our do-not-contact list</h2><span className="muted">{data.own.length} number{data.own.length === 1 ? '' : 's'}</span></div>
      <p className="muted" style={{ marginTop: 0 }}>Anyone who asked Handpicked or a business to stop. Responders can add to it from a lead too.</p>
      <div className="form-grid" style={{ alignItems: 'end' }}>
        <Field label="Phone number" id="own-n" type="tel" value={n} onChange={(e) => setN(e.target.value)} />
        <Field label="Why (optional)" id="own-why" value={why} onChange={(e) => setWhy(e.target.value)} />
        <div><Button onClick={add} disabled={n.replace(/\D/g, '').length < 10}>Add</Button></div>
      </div>
      {data.own.length > 0 && (
        <div className="bill-list" style={{ marginTop: 'var(--s-5)' }}>
          {data.own.map((o) => (
            <div key={o.id} className="bill-row">
              <span className="num">{phone(o.number)}</span>
              <span className="muted">{[o.reason, o.added_by_name ? `added by ${o.added_by_name}` : null, dateShort(o.created_at)].filter(Boolean).join(' · ')}</span>
              <Button variant="ghost" onClick={() => setRemoving(o.id)}>Remove</Button>
            </div>
          ))}
        </div>
      )}
      {removing && <ConfirmDialog title="Take this number off the list?" tone="neg" confirmLabel="Remove" body="Leads with it will reach businesses again. Only do this if they asked to be contacted again."
        onConfirm={async () => { try { await del(`/api/dnc/own/${removing}`); setRemoving(null); onChanged() } catch (e) { toast((e as Error).message, 'neg') } }} onCancel={() => setRemoving(null)} />}
    </Panel>
  )
}
